import { palette } from '@livable/saas-react-components';
import { makeStyles } from '@mui/styles';
/** Teal, light enough to sit behind dark teal text: a picked chip, the category being looked at. */
const PICKED_FILL = '#E6F4F5';
const PICKED_BORDER = '#A9D9DB';
const useStyles = makeStyles(theme => ({
  /**
   * "+ Filter": neutral white beside the search field, as tall as it. `align-self: stretch` keeps it level with the
   * field instead of hard-coding the input height.
   */
  addFilterButton: {
    '&.MuiButton-root': {
      flexShrink: 0,
      alignSelf: 'stretch',
      minHeight: 36,
      padding: '0 12px',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '16px',
      color: palette.shades[800],
      backgroundColor: palette.shades[100],
      border: "1px solid ".concat(palette.shades[300]),
      boxShadow: 'none',
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: 6
      },
      '& .MuiSvgIcon-root': {
        width: 18,
        height: 18
      },
      '&:hover': {
        backgroundColor: palette.shades[200],
        borderColor: palette.shades[400],
        boxShadow: 'none'
      }
    },
    // The toolbar paints every button icon white for the contained buttons; this one stays grey.
    '&&& path': {
      fill: palette.shades[700]
    }
  },
  // Wide enough for a long service provider beside the categories, never wider than a phone.
  panelPaper: {
    width: 'min(580px, calc(100vw - 32px))',
    maxHeight: 'min(520px, calc(100vh - 32px))',
    display: 'flex',
    flexDirection: 'column'
  },
  panelBody: {
    display: 'grid',
    gridTemplateColumns: '184px minmax(0, 1fr)',
    minHeight: 0,
    flex: '1 1 auto',
    // On a phone the categories run across the top and the options take the width under them.
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  categories: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 8,
    borderRight: "1px solid ".concat(palette.shades[300]),
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      overflowX: 'auto',
      overflowY: 'hidden',
      borderRight: 'none',
      borderBottom: "1px solid ".concat(palette.shades[300])
    }
  },
  category: {
    '&.MuiButtonBase-root': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 8,
      minHeight: 40,
      padding: '0 10px',
      borderRadius: 4,
      textAlign: 'left',
      fontSize: 14,
      color: palette.shades[800],
      flexShrink: 0,
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: palette.shades[200]
      }
    }
  },
  categoryCurrent: {
    '&.MuiButtonBase-root': {
      backgroundColor: PICKED_FILL,
      color: palette.primary.dark,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: PICKED_FILL
      }
    }
  },
  categorySummary: {
    fontSize: 12,
    fontWeight: 400,
    color: palette.shades[700],
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  optionsPane: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '12px 16px',
    minWidth: 0,
    minHeight: 0
  },
  optionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    minHeight: 32
  },
  optionsTitle: {
    fontWeight: 600
  },
  optionsHint: {
    fontSize: 12,
    color: palette.shades[700]
  },
  // The theme gives text buttons a white fill and every button a hover shadow, which drew a box round these three.
  clearButton: {
    '&.MuiButton-root': {
      minWidth: 0,
      padding: '2px 6px',
      fontSize: 13,
      fontWeight: 600,
      color: palette.primary.dark,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: PICKED_FILL,
        boxShadow: 'none'
      }
    }
  },
  search: {
    '& .MuiOutlinedInput-root': {
      height: 36,
      fontSize: 14
    }
  },
  // One column that scrolls. MUI's RadioGroup wraps by default, which turned a long list into clipped columns.
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    overflowY: 'auto',
    maxHeight: 320,
    '& .MuiFormControlLabel-root': {
      marginLeft: -6,
      marginRight: 0,
      minHeight: 36
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& .MuiCheckbox-root, & .MuiRadio-root': {
      padding: 6
    }
  },
  periodReadout: {
    fontSize: 14,
    fontWeight: 600,
    color: palette.shades[800]
  },
  /**
   * The Service period calendar, drawn into the pane. The picked days in the panel's own teal; the days between them in
   * its light fill with dark text, which reads where white on the light teal the other range pickers use does not.
   */
  periodCalendar: {
    '& .react-datepicker': {
      fontFamily: palette.fontFamilies.poppins,
      border: "1px solid ".concat(palette.shades[300]),
      borderRadius: 4
    },
    '& .react-datepicker__header': {
      backgroundColor: palette.shades[200],
      borderBottom: "1px solid ".concat(palette.shades[300])
    },
    '& .react-datepicker__current-month': {
      fontSize: 14
    },
    '& .react-datepicker__header__dropdown': {
      display: 'flex',
      justifyContent: 'center',
      gap: 6,
      margin: '6px 0',
      '& select': {
        fontFamily: 'inherit',
        fontSize: 13,
        padding: '2px 4px',
        borderRadius: 4,
        border: "1px solid ".concat(palette.shades[400])
      }
    },
    '& .react-datepicker__day-name, & .react-datepicker__day': {
      width: 32,
      lineHeight: '32px',
      margin: 1,
      fontSize: 13
    },
    '& .react-datepicker__day': {
      borderRadius: 4,
      color: palette.shades[800]
    },
    // Hover only where it says nothing else: over a picked day it read as the pick being undone.
    '& .react-datepicker__day:not(.react-datepicker__day--in-range):not(.react-datepicker__day--selected):not(.react-datepicker__day--in-selecting-range):hover': {
      backgroundColor: palette.shades[250]
    },
    '& .react-datepicker__day--in-range, & .react-datepicker__day--in-selecting-range': {
      backgroundColor: PICKED_FILL,
      color: palette.primary.dark
    },
    '& .react-datepicker__day--selected, & .react-datepicker__day--range-start, & .react-datepicker__day--range-end, & .react-datepicker__day--selecting-range-start': {
      backgroundColor: palette.primary.dark,
      color: palette.common.white,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: palette.primary.dark
      }
    },
    '& .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--in-range)': {
      backgroundColor: palette.shades[250],
      color: palette.shades[800]
    },
    '& .react-datepicker__day--outside-month': {
      color: palette.shades[600]
    },
    '& .react-datepicker__navigation-icon::before': {
      borderColor: palette.primary.dark
    }
  },
  optionsMessage: {
    fontSize: 14,
    color: palette.shades[700],
    padding: '8px 0'
  },
  optionsFailure: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  },
  panelActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  cancelButton: {
    '&.MuiButton-root': {
      color: palette.shades[800],
      fontWeight: 600,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: PICKED_FILL,
        boxShadow: 'none'
      }
    }
  },
  panelFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    borderTop: "1px solid ".concat(palette.shades[300])
  },
  // The chips under the search box: one per filter in use, then Clear all.
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: "1px solid ".concat(palette.shades[300])
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    height: 32,
    borderRadius: 16,
    backgroundColor: PICKED_FILL,
    border: "1px solid ".concat(PICKED_BORDER),
    color: palette.primary.dark
  },
  chipLabel: {
    '&.MuiButtonBase-root': {
      height: '100%',
      padding: '0 4px 0 12px',
      borderRadius: '16px 0 0 16px',
      fontSize: 13,
      color: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      lineHeight: '30px'
    }
  },
  chipName: {
    fontWeight: 600
  },
  chipRemove: {
    '&.MuiIconButton-root': {
      width: 26,
      height: 26,
      marginRight: 3,
      padding: 4,
      color: 'inherit'
    },
    '& .MuiSvgIcon-root': {
      width: 14,
      height: 14
    },
    '&&& path': {
      fill: palette.primary.dark
    }
  },
  clearAllButton: {
    '&.MuiButton-root': {
      minWidth: 0,
      padding: '4px 8px',
      fontSize: 13,
      fontWeight: 600,
      color: palette.primary.dark,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: PICKED_FILL,
        boxShadow: 'none'
      }
    }
  },
  // A column header's label and its funnel, in the header's own type.
  headerLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2
  },
  headerFilterButton: {
    '&.MuiIconButton-root': {
      padding: 3,
      borderRadius: 4
    },
    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16
    },
    '&&& path': {
      fill: palette.shades[700]
    }
  },
  // Filled while its filter is on, so a filtered column reads as one at a glance.
  headerFilterButtonActive: {
    '&.MuiIconButton-root': {
      backgroundColor: palette.primary.dark,
      '&:hover': {
        backgroundColor: palette.primary.dark
      }
    },
    '&&& path': {
      fill: palette.common.white
    }
  }
}));
export default useStyles;